import type { ShareProgress } from '../share-criteria-types.ts';

import {
	DB_CELLS,
	DB_ROWS,
	FIRST_THRESHOLD,
	LIVE_DOC_WORDS,
	WHITEBOARD_OBJECTS,
} from './thresholds.ts';

/**
 * Pure predicate: returns whether the given normalized progress satisfies the
 * per-surface share criteria for the TWCG-2055 experiment.
 *
 * `isFirstTrigger` selects which threshold set applies for the content type in
 * `progress`:
 *
 * - First trigger (a content type that has not previously fired its
 *   "criteria met" trigger): one unit of progress is enough —
 *   - live-doc: word-delta >= FIRST_THRESHOLD
 *   - database: rows >= FIRST_THRESHOLD OR filled cells >= FIRST_THRESHOLD
 *   - whiteboard: elements >= FIRST_THRESHOLD
 * - Subsequent trigger (the content type has fired before): the original,
 *   higher thresholds —
 *   - live-doc: word-delta >= LIVE_DOC_WORDS
 *   - database: (rows > DB_ROWS AND at least one cell filled) OR filled cells > DB_CELLS
 *   - whiteboard: elements >= WHITEBOARD_OBJECTS
 *
 * No React, no timers — safe to reuse from any layer (including Phase 6).
 */
export function evaluateShareCriteria(progress: ShareProgress, isFirstTrigger: boolean): boolean {
	switch (progress.type) {
		case 'live-doc':
			return isFirstTrigger
				? progress.wordDelta >= FIRST_THRESHOLD
				: progress.wordDelta >= LIVE_DOC_WORDS;
		case 'database':
			if (isFirstTrigger) {
				return progress.rows >= FIRST_THRESHOLD || progress.filledCells >= FIRST_THRESHOLD;
			}
			return (
				(progress.rows > DB_ROWS && progress.filledCells >= 1) || progress.filledCells > DB_CELLS
			);
		case 'whiteboard':
			return isFirstTrigger
				? progress.elements >= FIRST_THRESHOLD
				: progress.elements >= WHITEBOARD_OBJECTS;
		default: {
			// Exhaustiveness guard: if a new content type is added to the union,
			// this line fails to compile until the switch is updated.
			const _exhaustive: never = progress;
			return _exhaustive;
		}
	}
}
